import { safeDecimal } from "./decimal-input";
import Decimal from "decimal.js";

// Hyperliquid perp price rule: integer, or ≤ 5 significant figures and ≤ (6 − szDecimals) decimals.
// Price-dependent, so it can't be cached as a fixed tick size.
const MAX_SIGNIFICANT_FIGURES = 5;
const PERP_MAX_DECIMALS = 6;

export const isValidPerpPrice = (price: Decimal, szDecimals: number): boolean => {
  if (price.isInteger()) return true;
  return price.decimalPlaces() <= PERP_MAX_DECIMALS - szDecimals && price.sd() <= MAX_SIGNIFICANT_FIGURES;
};

/** Decimals the rule allows at this price's magnitude. */
export const perpPriceDecimals = (price: Decimal, szDecimals: number): number => {
  // `e` is floor(log10(|price|)): 99.953 → 1, 0.0123 → -2.
  const sigFigDecimals = MAX_SIGNIFICANT_FIGURES - (price.e + 1);
  return Math.max(0, Math.min(PERP_MAX_DECIMALS - szDecimals, sigFigDecimals));
};

/** Rounds to the finest price the rule allows at this magnitude; the result always satisfies {@link isValidPerpPrice}. */
export const roundPerpPrice = (
  price: Decimal,
  szDecimals: number,
  rounding: Decimal.Rounding = Decimal.ROUND_HALF_UP,
): Decimal => price.toDecimalPlaces(perpPriceDecimals(price, szDecimals), rounding);

/**
 * String form for form inputs and submit payloads. Returns the input untouched when it is
 * empty or `szDecimals` is unresolved (precision still loading).
 */
export const alignPerpPrice = (value: string | undefined, szDecimals: number | undefined): string | undefined => {
  if (!value || szDecimals == null) return value;
  return roundPerpPrice(safeDecimal(value, "0"), szDecimals).toString();
};
